import { dayInWords } from '../commissions/unpriced';
import { fill, t } from '../strings';
import { calendarDaysBetween } from './documentDate';

/**
 * 🔒 The warning on a document's first send when its date is old (piece 22;
 * decision 1227, T421). The product owner, 2026-09-27: *"add a warning if
 * sending a invoice for first time and the date is old"*. A customer receiving
 * a fresh document dated weeks ago counts its terms from that date, so the send
 * window names the date and the gap, and offers Send Anyway and Change the Date.
 * A resend warns nothing, because the customer already holds the date.
 */

/** 🔒 The days a document's date may stand before today on its first send, a Settings › Accounting row seeded 7 (piece 15). */
export const STALE_SEND_WARNING_DAYS_KEY = 'documents.staleSendWarningDays';
export const STALE_SEND_WARNING_DAYS_DEFAULT = 7;

/** The documents whose first send is warned: the three the mail client sends, and the Invoice's Issue. */
export type StaleSendDocument = 'Quote' | 'SalesOrder' | 'PurchaseOrder' | 'Invoice';

export interface StaleSendWarning {
  /** `YYYY-MM-DD`, the document's own date. */
  documentDate: string;
  /** Whole calendar days from the document's date to today. */
  days: number;
  /** The Settings row's figure the gap was measured against. */
  thresholdDays: number;
  /** The sentence the send window shows and the send-anyway audit row carries. */
  sentence: string;
}

const NAMES: Readonly<Record<StaleSendDocument, Parameters<typeof t>[0]>> = {
  Quote: 'document.staleSend.quote',
  SalesOrder: 'document.staleSend.salesOrder',
  PurchaseOrder: 'document.staleSend.purchaseOrder',
  Invoice: 'document.staleSend.invoice',
};

/**
 * The warning, or null: only on a first send, and only where the date stands
 * more than the threshold's days before today. A date in the future, today's
 * date, and a gap of exactly the threshold warn nothing.
 */
export function staleSendWarning(input: { documentType: StaleSendDocument; firstSend: boolean; documentDate: string | null; today: string; thresholdDays: number }): StaleSendWarning | null {
  if (!input.firstSend || !input.documentDate) return null;
  const days = calendarDaysBetween(input.documentDate.slice(0, 10), input.today.slice(0, 10));
  if (!(days > input.thresholdDays)) return null;
  const sentence = fill(t('document.staleSend.sentence'), { document: t(NAMES[input.documentType]), date: dayInWords(input.documentDate ?? ''), days: String(days) });
  return { documentDate: input.documentDate.slice(0, 10), days, thresholdDays: input.thresholdDays, sentence };
}

/** The Settings row read as a number: a whole number of days, zero or more; anything else reads as the seeded figure. */
export function staleSendThreshold(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : STALE_SEND_WARNING_DAYS_DEFAULT;
}

/** The refusal the Settings screen and the write make: a whole number of days from 0 to 365. */
export function staleSendDaysProblem(value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 365) return t('document.staleSend.daysProblem');
  return null;
}

/** 🔒 The field the send-anyway audit row carries the warning under (decision 1227). */
export const STALE_SEND_AUDIT_FIELD = 'staleSendWarning';
